import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Map as MapIcon,
  MapPin,
  Save,
  History,
  AlertTriangle,
  Loader2,
  CheckCircle2,
  Upload,
  RefreshCw,
  AlertCircle,
} from 'lucide-react';
import { Regional, HistoryEntry, REGIAO_UL_OPTIONS } from './types';
import { fetchRegionais, batchUpdateRegionais, fetchRegionaisHistory } from './api';
import { StatsCard, RegionCard, REGION_COLORS } from './StatsCard';
import { SearchInput } from './SearchInput';
import { RegionaisTable } from './RegionaisTable';
import { HistoryModal } from './HistoryModal';
import { ImportModal } from './ImportModal';

const RegionaisScreen: React.FC = () => {
  const [regionais, setRegionais] = useState<Regional[]>([]);
  const [editedRegions, setEditedRegions] = useState<Map<string, string>>(
    () => new Map<string, string>()
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [historyData, setHistoryData] = useState<HistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);

  const loadRegionais = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const data = await fetchRegionais();
      setRegionais(data);
    } catch {
      setLoadError('Erro ao carregar as regionais. Verifique a conexao e tente novamente.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRegionais();
  }, [loadRegionais]);

  const getEffectiveRegion = useCallback(
    (r: Regional) => editedRegions.get(r.idDistribuidor) ?? r.regiaoUL,
    [editedRegions]
  );

  const definedRegionsSet = useMemo(
    () => new Set(REGIAO_UL_OPTIONS.filter((o) => o !== 'Não definido')),
    []
  );

  const filteredRegionais = useMemo(() => {
    let result = regionais;

    if (selectedRegion) {
      if (selectedRegion === 'Não definido') {
        result = result.filter((r) => {
          const region:any = getEffectiveRegion(r);
          return !region || !definedRegionsSet.has(region);
        });
      } else {
        result = result.filter((r) => getEffectiveRegion(r) === selectedRegion);
      }
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (r) =>
          r.idDistribuidor.toLowerCase().includes(term) ||
          r.nomeCompletoDistribuidor.toLowerCase().includes(term) ||
          r.regiaoUL.toLowerCase().includes(term) ||
          r.estado.toLowerCase().includes(term)
      );
    }

    return result;
  }, [regionais, searchTerm, selectedRegion, getEffectiveRegion, definedRegionsSet]);

  const regionDistribution = useMemo(() => {
    const dist: Record<string, number> = {};
    REGIAO_UL_OPTIONS.forEach((o) => { dist[o] = 0; });
    regionais.forEach((r) => {
      const region:any = getEffectiveRegion(r);
      if (region && definedRegionsSet.has(region)) {
        dist[region] += 1;
      } else {
        dist['Não definido'] += 1;
      }
    });
    return dist;
  }, [regionais, getEffectiveRegion, definedRegionsSet]);

  const defaultRegionColor = REGION_COLORS['Não definido'];

  const handleRegionChange = useCallback(
    (idDistribuidor: string, newRegion: string) => {
      const original = regionais.find((r) => r.idDistribuidor === idDistribuidor);
      if (!original) return;

      const next = new Map(editedRegions);
      if (newRegion === original.regiaoUL) {
        next.delete(idDistribuidor);
      } else {
        next.set(idDistribuidor, newRegion);
      }
      setEditedRegions(next);
      setSaveSuccess(false);
      setSaveError(null);
    },
    [regionais, editedRegions]
  );

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    const items = Array.from(editedRegions.entries()).map(([idDistribuidor, regional_ul]) => ({
      id_distribuidor: idDistribuidor,
      regional_ul,
    }));

    try {
      const res = await batchUpdateRegionais(items);

      if (!res.success) {
        setSaveError(res.error || 'Erro ao salvar as alteracoes');
      } else {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      }
    } catch {
      setSaveError('Falha na conexao com o servidor');
    }

    setEditedRegions(new Map());
    setIsSaving(false);
    await loadRegionais();
  };

  const handleOpenHistory = useCallback(async () => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    setHistoryData([]);
    try {
      const entries = await fetchRegionaisHistory();
      setHistoryData(entries);
    } catch {
      setHistoryData([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const handleImportComplete = async (items: { id_distribuidor: string; regional_ul: string }[]) => {
    if (items.length > 0) {
      await batchUpdateRegionais(items);
    }
    setImportOpen(false);
    await loadRegionais();
  };

  const changesCount = editedRegions.size;

  return (
    <>
      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <header className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
                  Regionais
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                  Gerencie as Regionais UL dos Distribuidores
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={loadRegionais}
                  disabled={isLoading}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 hover:border-gray-300 transition-all disabled:opacity-50"
                  aria-label="Recarregar dados"
                >
                  <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={() => setImportOpen(true)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 hover:border-gray-300 transition-all"
                >
                  <Upload className="w-4 h-4" />
                  <span className="hidden sm:inline">Importar</span>
                </button>
                <button
                  onClick={handleOpenHistory}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 hover:border-gray-300 transition-all"
                >
                  <History className="w-4 h-4" />
                  <span className="hidden sm:inline">Histórico</span>
                </button>
                <button
                  onClick={handleSave}
                  disabled={changesCount === 0 || isSaving}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm shadow-blue-600/20 disabled:shadow-none"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Salvar Alterações
                    </>
                  )}
                </button>
              </div>
            </div>

            {changesCount > 0 && (
              <div className="mt-5 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-3">
                <div className="bg-amber-100 p-1.5 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                </div>
                <p className="text-sm font-medium text-amber-800">
                  {changesCount} {changesCount === 1 ? 'Regional com alteração pendente' : 'Regionais com alterações pendentes'}
                </p>
              </div>
            )}

            {saveSuccess && (
              <div className="mt-5 p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-3">
                <div className="bg-emerald-100 p-1.5 rounded-lg">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                </div>
                <p className="text-sm font-medium text-emerald-800">
                  Alterações salvas com sucesso
                </p>
              </div>
            )}

            {saveError && (
              <div className="mt-5 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
                <div className="bg-red-100 p-1.5 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-red-600" />
                </div>
                <p className="text-sm font-medium text-red-800">{saveError}</p>
              </div>
            )}
          </header>

          <div className="grid gap-4 grid-cols-2 lg:grid-cols-4 mb-4">
            <StatsCard
              title="Total Regionais"
              value={regionais.length}
              description="Distribuidores cadastrados"
              icon={<MapPin className="w-5 h-5" />}
            />
            <StatsCard
              title="Alterações Pendentes"
              value={changesCount}
              description={changesCount === 0 ? 'Nenhuma alteração' : 'Aguardando salvar'}
              icon={<AlertTriangle className="w-5 h-5" />}
              variant={changesCount > 0 ? 'warning' : 'default'}
            />
          </div>

          <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 mb-8">
            {REGIAO_UL_OPTIONS.map((region) => (
              <RegionCard
                key={region}
                name={region}
                count={regionDistribution[region] ?? 0}
                total={regionais.length}
                color={REGION_COLORS[region] ?? defaultRegionColor}
                icon={<MapIcon className="w-4 h-4" />}
                active={selectedRegion === region}
                onClick={() => setSelectedRegion((prev) => prev === region ? null : region)}
              />
            ))}
          </div>

          <div className="mb-5">
            <SearchInput
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Buscar por ID, nome, regional ou estado..."
            />
          </div>

          {loadError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex flex-col items-center gap-3 mb-5">
              <AlertCircle className="w-8 h-8 text-red-400" />
              <p className="text-sm text-red-700 text-center">{loadError}</p>
              <button
                onClick={loadRegionais}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Tentar novamente
              </button>
            </div>
          )}

          {isLoading && !loadError && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-16 flex flex-col items-center gap-3">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
              <p className="text-sm text-gray-500">Carregando regionais...</p>
            </div>
          )}

          {!isLoading && !loadError && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <RegionaisTable
                data={filteredRegionais}
                editedRegions={editedRegions}
                onRegionChange={handleRegionChange}
              />
            </div>
          )}
        </div>
      </div>

      <HistoryModal
        isOpen={historyOpen}
        onClose={() => setHistoryOpen(false)}
        history={historyData}
        loading={historyLoading}
      />

      <ImportModal
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        onImportComplete={handleImportComplete}
        currentRegionais={regionais}
      />
    </>
  );
};

export default RegionaisScreen;